// H7: a car created in the panel could never reach a customer. Public visibility needs
// `listing_status: 'published'` AND `operational_status: 'active'` to agree
// (`backend/src/modules/cars/service.ts:74`), while a new car defaults to `draft`/`inactive`
// and neither field had a control on any admin screen — so the only writer of `published`
// was the seed. Confirmed on deployed staging during the 055–058 Gate B run: a car created
// through the form was absent from the public list and 404 by id until a hand-written PATCH.
//
// The judgement lives in this pure module rather than in the Fleet page's JSX because "may
// this car go public, and if not why" is exactly the rule that rots untested — 050's
// pricing-summary and 058's `isUsable` set the precedent, and 057/058 both shipped defects
// that had lived inside a render.
//
// Enforcement is panel-only this slice (design D5): `PATCH /cars/:id` still accepts
// `listing_status: 'published'` for an unpriced car, and the backend already logs
// `published car has no resolvable display rate` (050). A server-side refusal narrows what
// the API accepts today, so it needs its own regression pass — recorded as a follow-up.

/**
 * The subset of the staff car row these rules read. Structural on purpose, so the Fleet
 * page's own `Car` interface satisfies it without either side importing the other.
 */
export interface PublishableCar {
  listing_status: string;
  operational_status: string;
  pricing?: { per_day_minor: number | null; per_hour_minor: number | null } | null;
}

export type PublishVerdict = { ok: true } | { ok: false; reason: string };

/** Operational states that are a deliberate decision, so publishing must not overwrite them. */
const WITHHELD_STATES = ['maintenance', 'retired'];

export function isPublished(car: PublishableCar): boolean {
  return car.listing_status === 'published';
}

/**
 * Whether this car may be put in front of customers, and if not, the sentence the operator
 * should read. The page never composes one of its own.
 *
 * "Priced" is `pricing` being present AND carrying at least one amount. 050 only emits a
 * `pricing` object when a usable rate card carries a base amount, so in practice a non-null
 * `pricing` has an amount — but the row type declares both amounts nullable, so the rule
 * states it rather than assuming it. Publishing an unpriced car yields a listing showing no
 * price whose Book Now 049 then refuses server-side with `BOOKING_NOT_PRICED` (design D3).
 *
 * The rate is checked before the operational state so the verdict is deterministic for a car
 * that fails both.
 */
export function canPublish(car: PublishableCar): PublishVerdict {
  const pricing = car.pricing;
  const hasAmount =
    !!pricing && (pricing.per_day_minor !== null || pricing.per_hour_minor !== null);
  if (!hasAmount) {
    return {
      ok: false,
      reason: 'No rate card — a customer would see no price. Add one before publishing.',
    };
  }
  if (WITHHELD_STATES.includes(car.operational_status)) {
    return {
      ok: false,
      reason: `This car is ${car.operational_status} — publishing would return it to service.`,
    };
  }
  return { ok: true };
}

/**
 * Publish sets BOTH fields in one request (design D2) so it cannot half-succeed and recreate
 * the published-but-invisible state H7 is. "Publish" means "a customer can see this" or it
 * means nothing.
 */
export const PUBLISH_PATCH = {
  listing_status: 'published',
  operational_status: 'active',
} as const;

/** Withdrawing a listing leaves `operational_status` alone — it is not a service decision. */
export const UNPUBLISH_PATCH = { listing_status: 'unpublished' } as const;
